export const GST_RATE = 0.1;

export function roundCurrency(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function calculateGst(amountExGst: number): number {
  return roundCurrency(amountExGst * GST_RATE);
}

export interface ClaimRetentionInput {
  subtotalExGst: number;
  retentionPct: number;
  remainingRetentionCapacity?: number;
  retentionReleaseAmount?: number;
}

export interface ClaimRetentionResult {
  subtotalExGst: number;
  subtotalExGstAfterRetention: number;
  retentionReleaseAmount: number;
  subtotalExGstWithRelease: number;
  gstAmount: number;
  subtotalIncGst: number;
  retentionAmount: number;
  totalIncGstAfterRetention: number;
}

export function calculateRetentionBeforeGst({
  subtotalExGst,
  retentionPct,
  remainingRetentionCapacity = Number.POSITIVE_INFINITY,
  retentionReleaseAmount = 0,
}: ClaimRetentionInput): ClaimRetentionResult {
  const normalizedSubtotal = roundCurrency(Math.max(0, subtotalExGst));
  const normalizedPct = Math.max(0, retentionPct);
  const normalizedRelease = roundCurrency(Math.max(0, retentionReleaseAmount));
  const rawRetention = roundCurrency(normalizedSubtotal * (normalizedPct / 100));
  const capacity = Number.isFinite(remainingRetentionCapacity)
    ? Math.max(0, remainingRetentionCapacity)
    : Number.POSITIVE_INFINITY;
  const retentionAmount = roundCurrency(Math.min(rawRetention, capacity));
  const subtotalExGstAfterRetention = roundCurrency(
    Math.max(0, normalizedSubtotal - retentionAmount),
  );
  const subtotalExGstWithRelease = roundCurrency(
    subtotalExGstAfterRetention + normalizedRelease,
  );
  const gstAmount = calculateGst(subtotalExGstWithRelease);
  const subtotalIncGst = roundCurrency(subtotalExGstWithRelease + gstAmount);

  return {
    subtotalExGst: normalizedSubtotal,
    subtotalExGstAfterRetention,
    retentionReleaseAmount: normalizedRelease,
    subtotalExGstWithRelease,
    gstAmount,
    subtotalIncGst,
    retentionAmount,
    totalIncGstAfterRetention: subtotalIncGst,
  };
}

export const calculateRetentionAfterGst = calculateRetentionBeforeGst;

export type ManualRetentionMode = "percent" | "amount";

export interface ManualClaimFinancialInput {
  claimedToDate?: number | null;
  variationsThisMonth?: number | null;
  variationsOutstanding?: number | null;
  claimThisMonth?: number | null;
  retentionMode?: ManualRetentionMode;
  retentionPct?: number | null;
  retentionAmount?: number | null;
  retentionReleaseAmount?: number | null;
  totalExcGstOverride?: number | null;
  totalIncGstOverride?: number | null;
  paidByBuilder?: number | null;
}

export interface ManualClaimFinancialResult {
  originalContractSum: number;
  claimedToDate: number;
  variationsThisMonth: number;
  variationsOutstanding: number;
  subtotal: number;
  claimThisMonth: number;
  retentionPct: number;
  retentionAmount: number;
  retentionReleaseAmount: number;
  retentionReleaseGst: number;
  retentionReleaseTotalIncGst: number;
  totalExcGst: number;
  gstAmount: number;
  totalIncGst: number;
  calculatedPaymentAmount: number;
  paidByBuilder: number;
  difference: number;
}

function normalizeMoney(value: number | null | undefined): number {
  return roundCurrency(Math.max(0, Number(value || 0)));
}

export function normalizeManualClaimFinancials({
  claimedToDate,
  variationsThisMonth,
  variationsOutstanding,
  claimThisMonth,
  retentionMode = "percent",
  retentionPct,
  retentionAmount,
  retentionReleaseAmount,
  totalExcGstOverride,
  totalIncGstOverride,
  paidByBuilder,
}: ManualClaimFinancialInput): ManualClaimFinancialResult {
  const normalizedClaimedToDate = normalizeMoney(claimedToDate);
  const normalizedVariationsThisMonth = normalizeMoney(variationsThisMonth);
  const normalizedVariationsOutstanding = normalizeMoney(variationsOutstanding);
  const fallbackClaimThisMonth = roundCurrency(
    normalizedClaimedToDate +
      normalizedVariationsThisMonth +
      normalizedVariationsOutstanding,
  );
  const normalizedClaimThisMonth =
    Number(claimThisMonth || 0) > 0
      ? normalizeMoney(claimThisMonth)
      : fallbackClaimThisMonth;
  const subtotal = normalizedClaimThisMonth;
  const normalizedRelease = normalizeMoney(retentionReleaseAmount);

  let normalizedRetentionPct = Math.max(0, Number(retentionPct || 0));
  let normalizedRetentionAmount = normalizeMoney(retentionAmount);
  if (retentionMode === "amount") {
    normalizedRetentionPct =
      subtotal > 0
        ? roundCurrency((normalizedRetentionAmount / subtotal) * 100)
        : 0;
  } else {
    const calc = calculateRetentionBeforeGst({
      subtotalExGst: subtotal,
      retentionPct: normalizedRetentionPct,
    });
    normalizedRetentionAmount = calc.retentionAmount;
  }

  normalizedRetentionAmount = Math.min(normalizedRetentionAmount, subtotal);
  const subtotalAfterRetention = roundCurrency(
    Math.max(0, subtotal - normalizedRetentionAmount),
  );
  const totalExcGstCalculated = roundCurrency(
    subtotalAfterRetention + normalizedRelease,
  );
  const totalExcGst =
    Number(totalExcGstOverride || 0) > 0
      ? normalizeMoney(totalExcGstOverride)
      : totalExcGstCalculated;
  const gstAmount = calculateGst(totalExcGst);
  const totalIncGstCalculated = roundCurrency(totalExcGst + gstAmount);
  const totalIncGst =
    Number(totalIncGstOverride || 0) > 0
      ? normalizeMoney(totalIncGstOverride)
      : totalIncGstCalculated;
  const normalizedPaidByBuilder = normalizeMoney(paidByBuilder);

  return {
    originalContractSum: normalizedClaimedToDate,
    claimedToDate: normalizedClaimedToDate,
    variationsThisMonth: normalizedVariationsThisMonth,
    variationsOutstanding: normalizedVariationsOutstanding,
    subtotal,
    claimThisMonth: normalizedClaimThisMonth,
    retentionPct: normalizedRetentionPct,
    retentionAmount: normalizedRetentionAmount,
    retentionReleaseAmount: normalizedRelease,
    retentionReleaseGst: calculateGst(normalizedRelease),
    retentionReleaseTotalIncGst: roundCurrency(normalizedRelease * (1 + GST_RATE)),
    totalExcGst,
    gstAmount,
    totalIncGst,
    calculatedPaymentAmount: totalIncGst,
    paidByBuilder: normalizedPaidByBuilder,
    difference: roundCurrency(totalIncGst - normalizedPaidByBuilder),
  };
}

export interface PaymentReconciliationInput {
  progressPaid: number;
  variationsPaid: number;
  retentionPct: number;
  retentionHeldEnabled: boolean;
  retentionReleased: number;
  amountReceived: number;
  /**
   * @deprecated SEQ-161 makes reconciled line items the retention source of
   * truth. Kept temporarily so older callers compile while they migrate.
   */
  retentionBaseExGst?: number;
}

export interface PaymentReconciliationResult {
  subtotalExcGst: number;
  gstAmount: number;
  subtotalIncGst: number;
  retentionHeldAmount: number;
  calculatedPaymentAmount: number;
  amountReceived: number;
  difference: number;
  progressPaymentPct: number;
}

export function calculatePaymentReconciliation({
  progressPaid,
  variationsPaid,
  retentionPct,
  retentionHeldEnabled,
  retentionReleased,
  amountReceived,
}: PaymentReconciliationInput): PaymentReconciliationResult {
  const subtotalExcGst = roundCurrency(progressPaid + variationsPaid);
  // SEQ-161: the reconciliation is the operational source of truth. Retention
  // is held on the reconciled progress/variation/hire subtotal, never on the
  // generated claim's stored totals.
  const retentionBase = Math.max(0, subtotalExcGst);
  const retentionHeldAmount = retentionHeldEnabled
    ? roundCurrency(retentionBase * (Math.max(0, retentionPct) / 100))
    : 0;
  const taxableSubtotal = roundCurrency(
    Math.max(0, subtotalExcGst - retentionHeldAmount) + Math.max(0, retentionReleased),
  );
  const gstAmount = calculateGst(taxableSubtotal);
  const subtotalIncGst = roundCurrency(taxableSubtotal + gstAmount);
  const calculatedPaymentAmount = subtotalIncGst;
  const normalizedReceived = roundCurrency(Math.max(0, amountReceived));

  return {
    subtotalExcGst,
    gstAmount,
    subtotalIncGst,
    retentionHeldAmount,
    calculatedPaymentAmount,
    amountReceived: normalizedReceived,
    difference: roundCurrency(calculatedPaymentAmount - normalizedReceived),
    progressPaymentPct:
      calculatedPaymentAmount > 0
        ? roundCurrency((normalizedReceived / calculatedPaymentAmount) * 100)
        : 0,
  };
}

export interface ConstructionIncomeInput {
  subtotal?: number | null;
  totalExcGst?: number | null;
  retentionAmount?: number | null;
}

export function deriveConstructionIncomeExGst({
  subtotal,
  totalExcGst,
  retentionAmount,
}: ConstructionIncomeInput): number {
  const normalizedSubtotal = Number(subtotal || 0);
  if (normalizedSubtotal > 0) {
    return roundCurrency(normalizedSubtotal);
  }

  // Legacy reports stored total_exc_gst after retention, so add retention back
  // only when subtotal was not captured.
  return roundCurrency(Number(totalExcGst || 0) + Number(retentionAmount || 0));
}

export interface RetentionBaseInput {
  subtotal?: number | null;
  totalExcGst?: number | null;
  retentionAmount?: number | null;
  retentionPct?: number | null;
}

/**
 * Derive the claim's ex-GST subtotal that retention should be charged on.
 * Retention held is "retentionPct% of the whole claim", independent of payments.
 * Prefers reconstructing the exact base the claim already used (retention amount
 * / pct), then falls back to the claim's ex-GST income.
 */
export function deriveRetentionBaseExGst({
  subtotal,
  totalExcGst,
  retentionAmount,
  retentionPct,
}: RetentionBaseInput): number {
  const amount = Number(retentionAmount || 0);
  const pct = Number(retentionPct || 0);
  if (amount > 0 && pct > 0) {
    return roundCurrency(amount / (pct / 100));
  }
  return deriveConstructionIncomeExGst({ subtotal, totalExcGst, retentionAmount });
}

export const deriveRetentionBaseIncGst = deriveRetentionBaseExGst;

export interface ResolveRetentionBaseInput extends RetentionBaseInput {
  retentionReleaseAmount?: number | null;
}

/**
 * SEQ-141: on a retention release claim the builder decides what to approve
 * and holds retention on the approved (paid) lines only, so no claim-level
 * base applies. The claim's total_exc_gst includes the release amount, which
 * must never be charged retention. Returning undefined makes
 * calculatePaymentReconciliation fall back to the paid-derived subtotal.
 * Normal claims keep the whole-claim base.
 */
export function resolveRetentionBaseExGst({
  retentionReleaseAmount,
  ...baseInput
}: ResolveRetentionBaseInput): number | undefined {
  if (Number(retentionReleaseAmount || 0) > 0) return undefined;
  return deriveRetentionBaseExGst(baseInput);
}
